import React from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const ContactContainer = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ContactSection = styled.section`
  margin-bottom: 3rem;

  h2 {
    text-align: center;
    margin-bottom: 2rem;
    color: #8b4513;
  }
`;

const ContactContent = styled.div`
  display: flex;
  gap: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
  background-color: #f9f9f9;
  padding: 2rem;
  border-radius: 10px;

  h3 {
    margin-bottom: 1rem;
    color: #8b4513;
  }

  p {
    margin-bottom: 1rem;
    line-height: 1.6;
  }
`;

const ContactForm = styled.form`
  flex: 1;
  background-color: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);

  h3 {
    margin-bottom: 1.5rem;
    color: #8b4513;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-family: inherit;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #d2691e;
    box-shadow: 0 0 0 2px rgba(210, 105, 30, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-family: inherit;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #d2691e;
    box-shadow: 0 0 0 2px rgba(210, 105, 30, 0.2);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: #d2691e;
    box-shadow: 0 0 0 2px rgba(210, 105, 30, 0.2);
  }
`;

const SubmitButton = styled.button`
  background-color: #d2691e;
  color: white;
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #a0522d;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.span`
  color: #d32f2f;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const SuccessMessage = styled(motion.div)`
  background-color: #4caf50;
  color: white;
  padding: 1rem;
  border-radius: 5px;
  margin-top: 1rem;
  text-align: center;
`;

const Contact = () => {
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const onSubmit = async (data) => {
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));

    console.log('Form submitted:', data);
    setIsSubmitted(true);
    reset();

    // Hide success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  return (
    <ContactContainer>
      <ContactSection as={motion.section} initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <h2>Get In Touch</h2>
        <ContactContent>
          <ContactInfo as={motion.div} initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <h3>Visit Us</h3>
            <p>123 Sweet Street<br />
            Bakery Town, BT 12345</p>
            <p>Phone: (555) 123-4567<br />
            Email: info@sweetdelightsbakery.com</p>
            <p>Hours:<br />
            Mon-Fri: 8am-6pm<br />
            Sat: 9am-5pm<br />
            Sun: Closed</p>
          </ContactInfo>
          <ContactForm as={motion.form} onSubmit={handleSubmit(onSubmit)} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
            <h3>Send Us a Message</h3>

            <FormGroup>
              <Label htmlFor="name">Name:</Label>
              <Input
                id="name"
                type="text"
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email:</Label>
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Please enter a valid email address'
                  }
                })}
              />
              {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="phone">Phone:</Label>
              <Input
                id="phone"
                type="tel"
                {...register('phone')}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="occasion">Occasion:</Label>
              <Select id="occasion" {...register('occasion')}>
                <option value="">Select occasion</option>
                <option value="wedding">Wedding</option>
                <option value="birthday">Birthday</option>
                <option value="corporate">Corporate Event</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message:</Label>
              <Textarea
                id="message"
                {...register('message', { required: 'Message is required' })}
              />
              {errors.message && <ErrorMessage>{errors.message.message}</ErrorMessage>}
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </SubmitButton>

            {isSubmitted && (
              <SuccessMessage
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                Thank you for your message! We'll get back to you soon.
              </SuccessMessage>
            )}
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ContactContainer>
  );
};

export default Contact;